import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import type { AuthLoginResponse } from "../auth/api-types";
import * as authStore from "../auth/authStore";
import { setAuthGetters } from "../auth/authBridge";
import type { AuthContextValue, AuthUser, LoginCredentials } from "../auth/types";
import { UserContext, type UserContextValue } from "../context/user-context";
import { handleError } from "../util/error-handler";
import { jsonPostData } from "../util/api-calls";
import { getEncryptionKeyIfSet } from "../util/config";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { setUser, setUserAccessToken } = useContext(UserContext) as UserContextValue;
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const isAuthenticated = Boolean(token);
  const role = (user?.role as string | null | undefined) ?? null;

  useEffect(() => {
    const loaded = authStore.loadSession();
    if (loaded) {
      setTokenState(loaded.access_token);
      setUserState((loaded.user as AuthUser | null | undefined) ?? null);
      if (setUser) setUser((loaded.user as AuthUser | null | undefined) ?? null);
      if (setUserAccessToken) setUserAccessToken(loaded.access_token);
    }
    setReady(true);
  }, [setUser, setUserAccessToken]);

  const login = useCallback(
    (credentials: LoginCredentials) => {
      if (!credentials?.email || !credentials?.password) return;

      const encryptionKey = getEncryptionKeyIfSet();
      if (!encryptionKey) {
        message.error({
          content: "Cannot sign in: encryption key is missing. Please contact support.",
        });
        return;
      }

      const formData = new FormData();
      formData.append("email", credentials.email);
      formData.append("password", credentials.password);

      jsonPostData("admin/login", formData)
        .then((response: { data?: AuthLoginResponse }) => {
          const data = response?.data;
          const userData = data?.user;
          if (!userData) {
            message.error({
              content: "Login succeeded but user data was missing. Please try again or contact support.",
            });
            return;
          }

          authStore.setSession(data);
          setTokenState(data?.access_token ?? null);
          setUserState(userData);
          if (setUser) setUser(userData);
          if (setUserAccessToken) setUserAccessToken(data?.access_token ?? null);
          navigate("/home/teams");
        })
        .catch((error: unknown) => {
          const { userMessage } = handleError(error, {
            context: "src/context/authContext.tsx",
            showToast: false,
          });
          message.error({
            content: userMessage,
          });
        });
    },
    [navigate, setUser, setUserAccessToken],
  );

  const logout = useCallback(() => {
    const refresh = authStore.getRefreshToken();
    const goLogin = () => {
      authStore.clearSession();
      setTokenState(null);
      setUserState(null);
      if (setUser) setUser(null);
      if (setUserAccessToken) setUserAccessToken(null);
      window.location.href = "/";
    };

    if (!refresh || typeof refresh !== "string" || refresh.trim() === "") {
      goLogin();
      return;
    }

    jsonPostData("user/logout", { refresh_token: refresh })
      .then(goLogin)
      .catch((error: unknown) => {
        const { userMessage } = handleError(error, { context: "src/context/authContext.tsx" });
        message.warning({
          content: userMessage,
        });
        goLogin();
      });
  }, [setUser, setUserAccessToken]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    const newToken = await authStore.refreshToken();
    if (newToken) {
      setTokenState(newToken);
      if (setUserAccessToken) setUserAccessToken(newToken);
      return newToken;
    }

    setTokenState(null);
    setUserState(null);
    if (setUser) setUser(null);
    if (setUserAccessToken) setUserAccessToken(null);
    return null;
  }, [setUser, setUserAccessToken]);

  useEffect(() => {
    setAuthGetters({
      getAccessToken: () => token,
      getRefreshToken: refreshToken,
    });
    return () => setAuthGetters(null);
  }, [token, refreshToken]);

  const value: AuthContextValue = {
    user,
    token,
    role,
    isAuthenticated,
    ready,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
